/**
 * "Send welcome email" (CC 0168, 0170): one Microsoft-synced, active staff member with a
 * real address. Shared by the staff editor and People > person. One Idempotency-Key per
 * click; the button is disabled while the request is in flight, so a double click cannot
 * send twice. A 403 hides the action for the rest of the page's life.
 */
import { useState } from "react";
import { Btn, Icon } from "@v0y4ge/jrni-ui";
import { sendStaffWelcomeEmail } from "@v0y4ge/api-client";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { isValidEmail } from "../../lib/field-validation";
import type { StaffMember } from "./useStaff";

export function WelcomeEmailButton({ member }: { member: StaffMember }) {
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const eligible = member.directorySynced === true && member.status === "active" && isValidEmail(member.email ?? "");
  if (!eligible || forbidden || !can("identity.team.manage")) return null;
  const who = member.name || [member.firstName, member.lastName].filter(Boolean).join(" ") || member.email;
  const send = async () => {
    if (busy) return;
    setBusy(true);
    const key = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `welcome-${member.id}-${Date.now()}`;
    try {
      await sendStaffWelcomeEmail(member.id, { idempotencyKey: key });
      toast({ tone: "success", title: `Welcome email sent to ${who}` });
    } catch (e) {
      const err = e as { status?: number; message?: string };
      if (err?.status === 403) { setForbidden(true); return; }
      toast({ tone: "warn", title: "Welcome email not sent", subtitle: err?.message || String(e) });
    } finally { setBusy(false); }
  };
  return <Btn variant="secondary" icon={<Icon.mail size={14} />} disabled={busy} onClick={() => { void send(); }} data-testid="staff-welcome-email">{busy ? "Sending…" : "Send welcome email"}</Btn>;
}
